/* eslint-disable import/no-cycle */
/* eslint-disable consistent-return */
/* eslint-disable no-else-return */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-use-before-define */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Joi from 'joi';
import { useTranslation } from 'react-i18next';
import { useStoreActions, useStoreState } from '../../../../models/hooks';
import { ACTIVE_ADVANCED_SECTIONS_KEYS, ACTIVE_ADVANCED_SECTIONS_TITLES } from '../schemas/formAdvancedSections';

// ============================================================================
// Types & Constants
// ============================================================================

export enum ValidationTypeEnum {
  LINK = 'link',
  TEXT = 'text',
  LABEL = 'label',
  SECTION_NAME = 'section_name',
  TEMPLATE_NAME = 'template_name',
  DOCUMENT_PREVIEW_CONFIRM_TEXT = 'document_preview_confirm_text',
  DOCUMENT_PREVIEW_FILE = 'document_preview_file',
}

export type ValidationType =
  | ValidationTypeEnum.LINK
  | ValidationTypeEnum.TEXT
  | ValidationTypeEnum.LABEL
  | ValidationTypeEnum.SECTION_NAME
  | ValidationTypeEnum.TEMPLATE_NAME
  | ValidationTypeEnum.DOCUMENT_PREVIEW_CONFIRM_TEXT
  | ValidationTypeEnum.DOCUMENT_PREVIEW_FILE;

export type ValueType =
  | string
  | {
      file: {
        mimetype: string;
        size: number;
      };
    }
  | Record<string, string>;

export type Language = 'en' | 'fr' | 'es';

export const languages: readonly Language[] = ['en', 'fr', 'es'];

interface OptimizedErrorHandlingProps {
  targetId: string;
  innerPath: string;
  isAdvanced: boolean;
  validationType?: ValidationType;
  onFocus?: () => void;
  removeQuestions?: boolean;
  limit?: number;
}

export interface OptimizedErrorValue {
  message: string;
  type?: string;
}

// ============================================================================
// Hook
// ============================================================================

export default function useOptimizedErrorHandling({
  targetId,
  innerPath,
  isAdvanced,
  validationType,
  onFocus,
  limit = 0,
}: OptimizedErrorHandlingProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const previousErrorRef = useRef<string | undefined>(undefined);
  const { t } = useTranslation('hrFormTemplate');

  // Store state and actions
  const globalError = useStoreState((state) => state.formBuilder.errorsByTarget(targetId, innerPath));
  const { setError: setGlobalError, removeError } = useStoreActions((state) => state.formBuilder);
  const [localValidationError, setLocalValidationError] = useState<Joi.ValidationErrorItem | undefined>(undefined);

  // ============================================================================
  // Joi Schema Definition
  // ============================================================================

  const joiSchema = useMemo(() => {
    if (!validationType) return undefined;

    const getLimit = (defaultLimit: number) => (limit || defaultLimit) + 1;

    const createMultiLangSchema = (maxLength: number, errorKey: string) =>
      Joi.alternatives().try(
        Joi.object().pattern(
          Joi.string().valid(...languages),
          Joi.string()
            .max(maxLength)
            .allow('', null)
            .optional()
            .messages({
              'string.max': t(`validation.${errorKey}`, { limit: maxLength }),
            }),
        ),
      );

    const schemas: Record<ValidationType, Joi.AnySchema> = {
      [ValidationTypeEnum.LINK]: createMultiLangSchema(getLimit(100), 'linkMaxLength'),

      [ValidationTypeEnum.TEXT]: createMultiLangSchema(getLimit(500), 'textMaxLength'),

      [ValidationTypeEnum.TEMPLATE_NAME]: Joi.string()
        .max(getLimit(50))
        .messages({
          'string.max': t('validation.templateNameMaxLength', { limit: getLimit(50) }),
          'string.empty': t('validation.templateNameRequired'),
        }),

      [ValidationTypeEnum.LABEL]: Joi.string()
        .max(getLimit(50))
        .messages({
          'string.max': t('validation.labelMaxLength', { limit: getLimit(50) }),
          'string.empty': t('validation.labelRequired'),
        }),

      [ValidationTypeEnum.SECTION_NAME]: Joi.string()
        .max(getLimit(50))
        .required()
        .custom((value, helpers) => {
          if (!isAdvanced && (ACTIVE_ADVANCED_SECTIONS_KEYS.includes(value) || ACTIVE_ADVANCED_SECTIONS_TITLES.includes(value))) {
            return helpers.error('any.invalid');
          }
          return value;
        })
        .messages({
          'string.max': t('validation.sectionMaxLength', { limit: getLimit(50) }),
          'string.empty': t('validation.sectionRequired'),
          'any.required': t('validation.sectionRequired'),
          'any.invalid': t('validation.advancedSectionNotAllowed'),
        }),

      [ValidationTypeEnum.DOCUMENT_PREVIEW_CONFIRM_TEXT]: Joi.string()
        .max(getLimit(30))
        .required()
        .messages({
          'string.max': t('validation.confirmTextMaxLength', { limit: getLimit(30) }),
          'string.empty': t('validation.confirmTextRequired'),
          'any.required': t('validation.confirmTextRequired'),
        }),

      [ValidationTypeEnum.DOCUMENT_PREVIEW_FILE]: Joi.object({
        file: Joi.object({
          mimetype: Joi.string()
            .valid('application/pdf')
            .required()
            .messages({
              'any.only': t('validation.fileAccept'),
              'any.required': t('validation.fileRequired'),
            }),
          size: Joi.number()
            .max(2 * 1024 * 1024)
            .required()
            .messages({
              'number.max': t('validation.fileMaxSize', { limit: 2 }),
              'any.required': t('validation.fileRequired'),
            }),
        })
          .required()
          .messages({ 'any.required': t('validation.fileRequired') }),
      }).required(),
    };

    return schemas[validationType];
  }, [validationType, t, limit, isAdvanced]);

  // ============================================================================
  // Error Display Logic
  // ============================================================================

  const displayError = useMemo<string | undefined>(() => {
    const currentError = localValidationError?.message || globalError;

    if (!currentError) {
      previousErrorRef.current = undefined;
      return undefined;
    }

    if (previousErrorRef.current === currentError) {
      return previousErrorRef.current;
    }

    previousErrorRef.current = currentError;
    return currentError;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValidationError?.message, globalError]);

  const hasError = !!displayError;

  // ============================================================================
  // Effects
  // ============================================================================

  useEffect(() => {
    if (displayError) {
      scrollToId(targetId);
    }
  }, [targetId, displayError]);

  function scrollToId(id: string) {
    const el = document.getElementById(id);
    const container = document.getElementById('hr-form-builder-scroll-view');

    if (!el || !container) return;
    const y = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 20;
    container.scrollTo({
      top: y,
      behavior: 'smooth',
    });
  }

  useEffect(() => {
    if (!localValidationError) {
      // removeError({ targetId, innerPath });
      return;
    }

    setGlobalError({
      fullPath: localValidationError.context?.label ?? '',
      targetId,
      innerPath,
      message: localValidationError.message,
    });
  }, [localValidationError, setGlobalError, targetId, innerPath]);

  useEffect(() => {
    if (!hasError) return;

    if (onFocus) {
      onFocus();
    } else if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [hasError, onFocus]);

  // ============================================================================
  // Validation Function
  // ============================================================================

  const validate = useCallback(
    (valueToValidate: ValueType) => {
      if (!joiSchema) return;

      const { error: joiValidationError } = joiSchema.validate(valueToValidate, {
        abortEarly: false,
      });

      if (joiValidationError) {
        const validationErrorItem = {
          ...joiValidationError.details[0],
          innerPath,
        };

        setLocalValidationError(validationErrorItem);
        setGlobalError({
          targetId,
          innerPath,
          message: validationErrorItem.message,
          fullPath: validationErrorItem.context?.label ?? '',
        });

        return false;
      }

      setLocalValidationError(undefined);
      removeError({ targetId, innerPath });
      return true;
    },
    [joiSchema, innerPath, targetId, setGlobalError, removeError],
  );

  // ============================================================================
  // Return
  // ============================================================================

  return {
    validate,
    error: displayError,
    hasError,
    inputRef,
  };
}
